<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import UIKit, { type ITableColumn, type ITableSortSettings, type TTableRow } from '@pocketprep/ui-kit'
import SelectStudents from '@/components/SidePanels/SelectStudents.vue'
import type { Study } from '@pocketprep/types'
import { formatDate } from '@/utils'
import { groupModule } from '@/store/group/module'
import { examModule } from '@/store/exam/module'
import { studentModule } from '@/store/student/module'
import { organizationModule } from '@/store/organization/module'
import { toastModule } from '@/store/toast/module'
import { instructorModule } from '@/store/instructor/module'
import { licenseModule } from '@/store/license/module'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'

type TStudentStatusOptions = {
    id: string
    label: string
    enabled: boolean
}

const {
    SidePanel,
    Button: PocketButton,
    Radio: PocketRadio,
    Select: PocketSelect,
    Input: PocketInput,
    Table,
    Search,
    Checkbox,
    TablePagination,
    FilterDropdown,
    Pill,
    EmptyState,
    Tag,
} = UIKit

const props = defineProps<{
    groupId: string
}>()

const emit = defineEmits<{
    close: [value?: boolean]
}>()

const groupName = ref('')
const examPrep = ref<null | { value: string; label: string }>(null)
const usePermission = ref<null | { value: 1 | 2 }>(null)
const editPermission = ref<null | { value: 1 | 2 }>(null)
const activeTabName = ref('Group Details')
const studentIds = ref<string[]>([])
const isLoading = ref(false)
const showAddStudentsSidePanel = ref(false)
const tabs = [
    'Group Details',
    'Group Students',
]
const mappedExams = ref<{ label: string; value: string }[]>([])
const searchString = ref<string | null>(null)
const perPage = ref(10)
const currentPage = ref(0)
const studentColumns = ref<ITableColumn[]>([
    {
        isLabelHidden: true,
        propName: 'checkbox',
        styles: {
            'width': '40px',
        },
    }, {
        name: 'Student',
        propName: 'student',
        styles: {
            'width': '210px',
        },
    }, {
        name: 'Exam Date',
        propName: 'examDate',
        styles: {
            'display': 'block',
            'width': '89px',
        },
    }, {
        name: 'Status',
        propName: 'status',
        styles: {
            width: '76px',
        },
    }, {

        propName: 'remove',
        isLabelHidden: true,
        styles: {
            width: '81px',
        },
    },
])
const checkboxOptions = ref<TStudentStatusOptions[]>([
    { id: 'active', label: 'Active', enabled: false },
    { id: 'pending', label: 'Pending', enabled: false },
])
const rowHoverId = ref<string | number | null>(null)
const studentCheckedIds = ref<string[]>([])
const allStudentsSelected = ref(false)
const sortSettings = ref<ITableSortSettings>({ column: studentColumns.value[0], direction: 1 })
const searchDebounceTimer = ref<ReturnType<typeof setTimeout> | null>(null)

const closeAddStudentsSidePanel = () => {
    showAddStudentsSidePanel.value = false
}

const openAddStudentsSidePanel = () => {
    showAddStudentsSidePanel.value = true
}

const currentOrgGroups = computed(() => {
    return groupModule.getters.getCurrentOrgGroups()
})

const groupsStudentIds = computed(() => {
    return groupModule.getters.getGroupsStudentIds()
})

const currentOrgExams = computed(() => {
    return examModule.getters.getCurrentOrgExams()
})

const orgExamIdGuidLib = computed(() => {
    return examModule.getters.getOrgExamIdGuidLib()
})

const currentOrgStudents = computed(() => {
    return studentModule.getters.getCurrentOrgStudents()
})

const currentOrgStudentUsers = computed(() => {
    return studentModule.getters.getCurrentOrgStudentUsers()
})

const currentOrgStudentUEM = computed(() => {
    return studentModule.getters.getCurrentOrgStudentUEM()
})

const currentOrganization = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const currentOrgInstructor = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructor()
})

const currentOrgLicenses = computed(() => {
    return licenseModule.getters.getCurrentOrgLicenses()
})

const studentLicenseLib = computed(() => {
    const orgExamId = examPrep.value?.value
    if (!orgExamId) {
        return {}
    }
    const examGuid = orgExamIdGuidLib.value[orgExamId]
    return currentOrgLicenses.value.reduce((acc, license) => {
        const orgStudentId = license.orgStudent?.objectId
        if (!orgStudentId || license.examGuid !== examGuid) {
            return acc
        }
        acc[orgStudentId] = license
        return acc
    }, {} as { [key: string]: Study.Class.LicenseJSON })
})

const yourStudentIds = computed(() => {
    const group = groupModule.getters.searchCurrentOrgGroups({
        type: 'system',
        examId: examPrep.value?.value,
        instructorId: currentOrgInstructor.value?.objectId,
    })
    return group?.length ? group[0]?.orgStudents.map(s => s.objectId) : []
})

const yourSelectedStudentIds = computed(() => {
    return currentOrgInstructor.value?.isAdmin
        ? studentIds.value
        : studentIds.value.filter(id => yourStudentIds.value?.includes(id))
})

const enabledFilterStatuses = computed(() => {
    return checkboxOptions.value.reduce<string[]>((acc, s) => {
        const first = s.label.split(' ')[0]
        if (first !== undefined) {
            acc.push(first)
        }
        return acc
    }, [])
})

const mappedStudents = computed(() => {
    const sortSettingsVal = sortSettings.value
    const examPrepVal = examPrep.value
    const instructor = currentOrgInstructor.value
    if (!instructor || !examPrepVal) {
        return []
    }

    const group = Object.values(currentOrgGroups.value).find(g =>
        g.orgExam.objectId === examPrepVal.value
        && g.type === 'system'
        && !g.owner
    )
    const activeGroupStudentIds = group && groupsStudentIds.value[group.objectId]

    return (activeGroupStudentIds || [])
        .reduce((acc, studentId) => {
            const student = currentOrgStudents.value[studentId]
            const studentUser = student?.user && currentOrgStudentUsers.value[student.user.objectId]
            const studentLicense = studentLicenseLib.value[studentId]
            if (!studentLicense) {
                return acc
            }
            let hideRemoveButton = true
            const styles: { [key: string]: string } = { cursor: 'pointer', userSelect: 'none' }
            if (rowHoverId.value === studentId) {
                styles.backgroundColor = `${BrandColors.grayBackground}60`
                hideRemoveButton = false
            }
            if (student && studentUser) {
                const studentName = (student.displayFirstName || student.displayLastName)
                    ? [ student.displayFirstName, student.displayLastName ].join(' ')
                    : [ studentUser.firstName, studentUser.lastName ].join(' ')
                const sortName = (student.displayFirstName || student.displayLastName)
                    ? [ student.displayLastName, student.displayFirstName ].join(' ')
                    : [ studentUser.lastName, studentUser.firstName ].join(' ')
                const startDate = studentLicense.redeemedDate
                    ? formatDate(studentLicense.redeemedDate.iso)
                    : '--'
                acc.push({
                    id: student.objectId,
                    student: studentName,
                    startDate,
                    examDate: '--',
                    sortName,
                    styles,
                    isPending: false,
                    hideRemoveButton,
                    checkboxDisabled: false,
                })
            } else {
                const id = studentLicense.orgStudent?.objectId
                const studentEmail = studentLicense.userEmail
                if (!id || !studentEmail) {
                    return acc
                }
                styles.color = BrandColors.ash || ''
                acc.push({
                    id,
                    student: studentEmail,
                    startDate: '--',
                    examDate: '--',
                    sortName: studentEmail,
                    styles,
                    isPending: true,
                    hideRemoveButton,
                    checkboxDisabled: false,
                })
            }
            return acc
        }, [] as ({
            id: string
            student: string
            startDate: string
            examDate: string
            sortName: string
            styles: { [key: string]: string }
            isPending: boolean
            hideRemoveButton: boolean
            checkboxDisabled: boolean
        })[])
        .filter(s => {
            const searchStringVal = searchString.value
            const studentStatus = s.isPending ? 'Pending' : 'Active'
            const matchesSearch = !searchStringVal || s.student.toLowerCase().includes(searchStringVal.toLowerCase())
            const matchesFilter = enabledFilterStatuses.value.length
                && enabledFilterStatuses.value.includes(studentStatus)
                || !enabledFilterStatuses.value.length
            return matchesSearch && matchesFilter
        })
        .sort((a, b) => {
            if (sortSettingsVal?.column) {
                if (sortSettingsVal.column.name === 'Student') {
                    return sortSettingsVal.direction * a.sortName.localeCompare(b.sortName)
                }
                if (sortSettingsVal.column.name === 'Start Date') {
                    const aStartDate = new Date(a.startDate).getTime() || 0
                    const bStartDate = new Date(b.startDate).getTime() || 0
                    return sortSettingsVal.direction * (bStartDate - aStartDate)
                }
                if (sortSettingsVal.column.name === 'Exam Date') {
                    const aExamDate = new Date(a.examDate).getTime() || 0
                    const bExamDate = new Date(b.examDate).getTime() || 0
                    return sortSettingsVal.direction * (bExamDate - aExamDate)
                }
            }
            return -1
        })
})

const mappedSelectedStudents = computed(() => {
    return mappedStudents.value.filter(s => studentIds.value.includes(s.id))
})

const isEmptyGroup = computed(() => {
    return mappedSelectedStudents.value.length === 0 && !searchString.value && !enabledFilters.value.length
})

const emptyStateTitle = computed(() => {
    return isEmptyGroup.value
        ? 'There\'s nobody here yet'
        : enabledFilters.value.length
            ? 'No results'
            : `No results for "${searchString.value}"`
})

const emptyStateMessage = computed(() => {
    return isEmptyGroup.value
        ? 'Get started by adding some students to this group.'
        : 'There are no results that match your filter and search criteria.'
})

const pagedSelectedStudents = computed(() => {
    const perPageVal = perPage.value
    const pagedStudents = mappedSelectedStudents.value
        .slice(currentPage.value * perPageVal, (currentPage.value * perPageVal) + perPageVal)
    if (!examPrep.value) {
        return pagedStudents
    }
    const examGuid = orgExamIdGuidLib.value[examPrep.value.value]
    return pagedStudents.map(pagedStudent => {
        const student = currentOrgStudents.value[pagedStudent.id]
        const studentUser = student?.user && currentOrgStudentUsers.value[student.user.objectId]
        if (!studentUser) {
            return pagedStudent
        }
        const studentUEM = currentOrgStudentUEM.value[studentUser.objectId]?.find(uem => uem.examGuid === examGuid)
        return {
            ...pagedStudent,
            examDate: studentUEM && studentUEM.scheduledExamDate
                ? formatDate(studentUEM.scheduledExamDate.iso)
                : pagedStudent.examDate,
        }
    })
})

watch(() => pagedSelectedStudents.value.length, () => {
    const perPageVal = perPage.value
    if (mappedSelectedStudents.value.length < perPageVal) {
        currentPage.value = 0
    }
})

const mappedYourStudents = computed(() => {
    return currentOrgInstructor.value?.isAdmin
        ? mappedStudents.value
        : mappedStudents.value.filter(s => yourStudentIds.value?.includes(s.id))
})

const activeCountForGroup = computed(() => {
    return mappedSelectedStudents.value.filter(mys => mys && !mys.isPending).length
})

const pendingCountForGroup = computed(() => {
    return mappedSelectedStudents.value.filter(mys => mys?.isPending).length
})

const enabledFilters = computed(() => {
    return enabledFilterStatuses.value.map(efs => ({
        id: efs.toLocaleLowerCase(),
        label: efs,
        showClose: true,
    }))
})

const studentsSelected = computed(() => {
    return (studentCheckedIds.value.length && mappedSelectedStudents.value.length
        && studentCheckedIds.value.length === mappedSelectedStudents.value.length)
        ? true
        : studentCheckedIds.value.length === 0 && !allStudentsSelected.value ? false : null
})

const setFilterOptions = (enabledFilterOption?: string | null) => {
    checkboxOptions.value = [
        {
            id: 'active',
            label: `Active (${activeCountForGroup.value})`,
            enabled: isFilterEnabled('active') || enabledFilterOption === 'active',
        },
        {
            id: 'pending',
            label: `Pending (${pendingCountForGroup.value})`,
            enabled: isFilterEnabled('pending') || enabledFilterOption === 'pending',
        },
    ]
}

const removeFilter = (filterId: string) => {
    checkboxOptions.value = checkboxOptions.value.map(o =>
        o.id === filterId ? { ...o, enabled: false } : o
    )
}

const isFilterEnabled = (status: string) => {
    return checkboxOptions.value.findIndex(s => s.id === status && s.enabled === true) > -1
}

const rowEnter = (row: TTableRow) => {
    rowHoverId.value = row.id
}

const rowLeave = (row: TTableRow) => {
    if (row.id === rowHoverId.value) {
        rowHoverId.value = null
    }
}

const rowClicked = (row: { id: string; checkboxDisabled?: boolean }) => {
    if (row.checkboxDisabled) {
        return studentCheckedIds.value
    }
    if (studentCheckedIds.value.includes(row.id)) {
        studentCheckedIds.value = studentCheckedIds.value.filter(id => id !== row.id)
    } else {
        studentCheckedIds.value.push(row.id)
    }
    return studentCheckedIds.value
}

const selectAllStudents = () => {
    if (studentsSelected.value || studentsSelected.value === null) {
        studentCheckedIds.value = []
    } else {
        studentCheckedIds.value = [ ...mappedSelectedStudents.value.map(mss => mss.id) ]
    }
}

const emitClose = () => {
    emit('close', true)
}

const fetchUEMForPagedStudents = async () => {
    await nextTick()
    if (!pagedSelectedStudents.value.length || !examPrep.value) {
        return
    }
    const studentsToFetch = pagedSelectedStudents.value.map(pagedStudent => pagedStudent.id)
    if (studentsToFetch.length) {
        await studentModule.actions.fetchOrgStudentUEMForStudents(studentsToFetch)
    }
}

onMounted(async () => {
    await Promise.all([
        groupModule.actions.fetchCurrentOrgGroups(),
        examModule.actions.fetchCurrentOrgExams(),
        studentModule.actions.fetchCurrentOrgStudents(),
        studentModule.actions.fetchCurrentOrgStudentUsers(),
        instructorModule.actions.fetchCurrentOrgInstructor(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
    ])

    const group = currentOrgGroups.value[props.groupId]
    if (!group) {
        throw new Error('EditGroup: No group found')
    }

    const exam = currentOrgExams.value[group.orgExam.objectId]
    if (!exam) {
        throw new Error('EditGroup: No group exam found')
    }

    const examOption = { value: exam.objectId, label: exam.displayName }
    examPrep.value = examOption
    mappedExams.value = [ examOption ]
    groupName.value = group.name
    studentIds.value = group.orgStudents.map(s => s.objectId)
    usePermission.value = group.type === 'public' ? { value: 1 } : { value: 2 }
    editPermission.value = group.isPubliclyEditable ? { value: 1 } : { value: 2 }

    setFilterOptions()
})

const updateSelectedStudents = (updatedStudentIds: string[]) => {
    studentIds.value = [ ...studentIds.value, ...updatedStudentIds ]
    showAddStudentsSidePanel.value = false
}

const changeActiveTab = (tabName: string) => {
    activeTabName.value = tabName
}

const removeStudent = (studentId: string) => {
    studentIds.value = studentIds.value.filter(si => si !== studentId)
    studentCheckedIds.value = studentCheckedIds.value.filter(sci => sci !== studentId)
}

const removeStudents = () => {
    const checkedSet = new Set(studentCheckedIds.value)
    studentIds.value = studentIds.value.filter(si => !checkedSet.has(si))
    studentCheckedIds.value = []
}

const updateSortSettings = (newSortSettings: ITableSortSettings) => {
    sortSettings.value = newSortSettings
}

const changePage = (newPage: number) => {
    currentPage.value = newPage
}

const submitDeleteGroup = async () => {
    if (!currentOrganization.value) {
        throw new Error('submitDeleteGroup: No current organization')
    }
    isLoading.value = true
    await groupModule.actions.deleteCompositeGroup({
        groupCompositeKey: groupModule.getters.getGroupCompositeKeyForGroupId(props.groupId) || '',
        organizationId: currentOrganization.value.objectId,
    })
    isLoading.value = false
    emitClose()
}

const submitUpdateGroup = async () => {
    if (!currentOrganization.value) {
        throw new Error('submitUpdateGroup: Unable to find current organization')
    }
    isLoading.value = true
    await groupModule.actions.updateCompositeGroup({
        groupCompositeKey: groupModule.getters.getGroupCompositeKeyForGroupId(props.groupId) || '',
        updateParams: {
            orgStudentIds: studentIds.value,
            name: groupName.value,
            type: usePermission.value?.value === 1 ? 'public' : 'private',
            isPubliclyEditable: editPermission.value?.value === 1 ? true : false,
        },
    })
    toastModule.actions.displayToast({ title: 'Changes saved.' })
    isLoading.value = false
    emitClose()
}

watch(studentIds, () => setFilterOptions)
watch(usePermission, (newVal) => {
    if (newVal?.value === 2) {
        editPermission.value = { value: 2 }
    }
}, { deep: true })
watch(currentPage, fetchUEMForPagedStudents)
watch(searchString, () => {
    if (searchDebounceTimer.value) {
        clearTimeout(searchDebounceTimer.value)
    }
    searchDebounceTimer.value = setTimeout(() => {
        fetchUEMForPagedStudents()
        searchDebounceTimer.value = null
    }, 1000)
})
watch(() => pagedSelectedStudents.value.length, async (newLength) => {
    if (newLength > 0 && examPrep.value) {
        await fetchUEMForPagedStudents()
    }
})
</script>

<template>
    <Portal to="sidepanel">
        <SidePanel
            :tabs="tabs"
            :active-tab-name="activeTabName"
            :stepped-tabs="false"
            :name="`Edit ${groupName} Group`"
            :isTeachSidePanel="true"
            class="edit-group"
            @close="emitClose"
            @selectTab="changeActiveTab"
        >
            <template v-if="activeTabName === 'Group Students'">
                <SelectStudents
                    v-if="showAddStudentsSidePanel"
                    :table-title="
                        `Select <span>${examPrep && examPrep.label}</span> students to add to ${groupName}`
                    "
                    :student-options="mappedYourStudents"
                    :initial-selected-student-ids="yourSelectedStudentIds"
                    @close="closeAddStudentsSidePanel"
                    @updateSelectedStudents="updateSelectedStudents"
                />
                <div class="edit-group__table-head">
                    <Search
                        v-model="searchString"
                        class="edit-group__table-search"
                        placeholder="Search"
                        type="white"
                        @input="setFilterOptions"
                    />
                    <FilterDropdown
                        v-model="checkboxOptions"
                        class="edit-group__filter"
                        filter-options-label="FILTER BY STATUS"
                        filterLabel=""
                        :radioEntries="[]"
                        :checkboxEntries="checkboxOptions"
                    />
                    <TablePagination
                        :per-page="perPage"
                        unit="students"
                        :current-page="currentPage"
                        :total="mappedSelectedStudents.length"
                        @changePage="changePage($event)"
                    />
                </div>
                <div
                    v-if="enabledFilters.length"
                    class="edit-group__filters">
                    <span class="edit-group__filters-label">Filters:</span>
                    <Pill
                        v-for="filter in enabledFilters"
                        :key="filter.id"
                        :show-close="true"
                        class="students-table__filter-chip"
                        @click="removeFilter(filter.id)"
                    >
                        {{ filter.label }}
                    </Pill>
                </div>
                <div class="edit-group__bulk-actions">
                    <Checkbox
                        :modelValue="studentsSelected"
                        class="edit-group__bulk-actions-checkbox"
                        @click="selectAllStudents"
                    />
                    <div
                        class="edit-group__bulk-actions-num-checked"
                    >
                        {{ studentCheckedIds.length }} of {{ mappedSelectedStudents.length }} selected
                    </div>
                    <PocketButton
                        class="add-group__remove"
                        type="tertiary"
                        :disabled="studentCheckedIds.length === 0"
                        @click="removeStudents"
                    >
                        Remove Student
                    </PocketButton>
                </div>
                <Table
                    key="edit-group__selected-students"
                    class="edit-group__students-table"
                    :rows="pagedSelectedStudents"
                    :columns="studentColumns"
                    :default-sort="sortSettings"
                    :sort-toggle-order="[ 1, -1 ]"
                    :show-header="false"
                    :table-cell-styles="{
                        margin: '0'
                    }"
                    :table-column-label-styles="{
                        margin: '0 auto',
                        paddingTop: '7px',
                        color: '#1F3545',
                        fontSize: '12px',
                    }"
                    @sort="updateSortSettings"
                    @rowEnter="rowEnter"
                    @rowLeave="rowLeave"
                    @rowClicked="rowClicked({
                        id: $event.id as string,
                        checkboxDisabled: $event.checkboxDisabled as boolean
                    })"
                >
                    <template #tableCellValue="{ row, column }">
                        <Checkbox
                            :modelValue="studentCheckedIds.includes(row.id)"
                            v-if="column.propName === 'checkbox'"
                            class="seats-table__checkbox"
                            :disabled="row.checkboxDisabled"
                        />
                        <PocketButton
                            v-if="column.propName === 'remove' && !row.hideRemoveButton"
                            class="add-group__remove"
                            type="tertiary"
                            @click.stop="removeStudent(row.id)"
                        >
                            Remove
                        </PocketButton>
                        <template v-if="column.propName === 'status'">
                            <Tag
                                v-if="row.isPending"
                                :isPendingTag="true"
                                size="small"
                            >
                                <template #tagMessage>
                                    Pending
                                </template>
                            </Tag>
                            <Tag
                                v-else
                                :isActiveTag="true"
                                size="small"
                            >
                                <template #tagMessage>
                                    Active
                                </template>
                            </Tag>
                        </template>
                    </template>
                    <template #tableRows>
                        <EmptyState
                            v-if="!mappedSelectedStudents.length"
                            class="edit-group__empty-state"
                            :title="emptyStateTitle"
                            :message="emptyStateMessage"
                        >
                            <template #imageRow>
                                <div />
                            </template>
                        </EmptyState>
                        <PocketButton
                            v-if="!mappedSelectedStudents.length"
                            class="edit-group__add-student-btn"
                            @click="openAddStudentsSidePanel"
                        >
                            Add Student
                        </PocketButton>
                    </template>
                </Table>
                <PocketButton
                    v-if="mappedSelectedStudents.length"
                    type="secondary"
                    class="edit-group__add-student"
                    @click="openAddStudentsSidePanel"
                >
                    + Add Student
                </PocketButton>
            </template>
            <template v-if="activeTabName === 'Group Details'">
                <div
                    class="edit-group__group-details"
                >
                    <label>Group Details</label>
                    <p>Name your new group &amp; edit permissions, then add students.</p>
                    <PocketInput
                        v-model="groupName"
                        label="Group Name"
                        class="edit-group__field"
                    />
                    <PocketSelect
                        v-model="examPrep"
                        label="Exam Prep"
                        placeholder="Select exam prep"
                        class="edit-group__field"
                        :data="mappedExams"
                        :disabled="true"
                    />
                    <PocketRadio
                        v-model="usePermission"
                        label="Who can <strong>use</strong> it?"
                        class="edit-group__field edit-group__use-permission"
                        :data="[
                            { label: 'Colleagues in organization', value: 1 },
                            { label: 'Just me', value: 2 }
                        ]"
                    />
                    <PocketRadio
                        v-model="editPermission"
                        label="Who can <strong>edit</strong> this group?"
                        class="edit-group__field edit-group__edit-permission"
                        :disabled="!usePermission || usePermission.value !== 1"
                        :data="[
                            { label: 'Colleagues in organization', value: 1 },
                            { label: 'Just me', value: 2 }
                        ]"
                    />
                </div>
            </template>
            <template #action>
                <PocketButton
                    type="primary"
                    :disabled="isLoading"
                    @click="submitUpdateGroup"
                >
                    Save Changes
                </PocketButton>
            </template>

            <template #details>
                <PocketButton
                    type="tertiary-red-small"
                    class="edit-group__delete"
                    :disabled="isLoading"
                    @click="submitDeleteGroup"
                >
                    Delete Group
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<style lang="scss" scoped>
.edit-group {
    :deep(*) {
        .uikit-side-panel__content {
            padding-top: 12px;
        }

        .uikit-table__column-labels {
            background-color: $barely-background;
            border-top: 1px solid $fog;
            height: 38px;
        }

        .uikit-side-panel__content--has-tabs {
            padding-left: 30px;
        }

        .uikit-side-panel__tab--stepper {
            margin-bottom: 0;
        }
        .uikit-side-panel__tab--stepper::after {
            bottom: 0;
        }
    }
    &__field {
        margin-bottom: 21px;
        max-width: 363px;
    }

    &__back-arrow {
        margin-right: 10px;
        transform: rotate(180deg);
    }

    &__add-student {
        padding: 12px 32px;
        font-weight: 600;
    }

    &__add-student-btn {
        display: table;
        margin: 22px auto;
    }

    &__table-head {
        display: flex;
        margin: 11px 11px 15px;
        justify-content: space-between;
        align-items: center;
    }

    &__table-search {
        width: 262px;
    }

    &__bulk-actions {
        display: flex;
        padding-left: 23px;
        margin-bottom: 11px;
    }

    &__bulk-actions-num-checked {
        font-size: 12px;
        line-height: 16px;
        margin: 2px 30px;
        margin-left: 23px;
    }

    &__students-table {
        padding-left: 11px;
    }

    &__filters {
        height: 30px;
        margin: 0 0 8px 3px;
    }

    &__filters-label {
        font-size: 13px;
        line-height: 22px;
        color: $slate-03;
        margin-right: 9px;
        display: inline-block;
        margin-left: 12px;
        vertical-align: top;
    }

    &__group-details {
        padding: 14px;
    }

    &__empty-state {
        margin: 0 auto;
        padding-top: 40px;
    }
}
</style>
